import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "react-router-dom";

import ViewDocumentModal from "../../pages/Documents/components/ViewDocumentModal";
import { useAskPerformYardStore } from "./AskPerformYardStore";
import { ChatContainer } from "./components/ChatContainer";
import ChatErrorBoundary from "./components/ChatErrorBoundary";
import ChatTrigger from "./components/ChatTrigger";
import { CompetencyCoachPopup } from "./components/CompetencyCoachPopup";
import { FormBuildingCoachPopup } from "./components/FormBuildingCoachPopup";
import { GoalCoachPopup } from "./components/GoalCoachPopup";

import {
	useAiSettings,
	useHasAccessibleDocuments,
	useHasGoalCategories,
	useRbacPrimaryRolePermissions,
} from "../../api/hooks";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";
import { useCompetencyAgentVisible } from "./hooks/useCompetencyAgentVisible";
import { useEnhancedAiPanelEnabled } from "./hooks/useEnhancedAiPanelEnabled";
import { useFormBuildingCoachEnabled } from "./hooks/useFormBuildingCoachEnabled";
import { useGoalCoachEnabled } from "./hooks/useGoalCoachEnabled";
import { useReportBuilderVisible } from "./hooks/useReportBuilderVisible";
import { useReportSave } from "./hooks/useReportSave";

import { getUserID } from "../../selectors/app";

import { isInIframe } from "../../utils/isInIframe";

import RouterTags from "../../constants/RouterTags";

/** Check if Ask PerformYard should be visible (used by FeedbackDrawers for positioning). */
export const useAskPerformYardVisible = (): boolean => {
	const { value: aiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const goalCoachEnabled = useGoalCoachEnabled();
	const formBuildingCoachEnabled = useFormBuildingCoachEnabled();
	const competencyAgentVisible = useCompetencyAgentVisible();
	const enhancedAiPanelEnabled = useEnhancedAiPanelEnabled();
	const { data: aiSettings } = useAiSettings();

	const askHrEnabled = aiSettings?.conversationalAiSettings?.askHrEnabled ?? false;
	const aiAssistedGoalCreationEnabled = aiSettings?.conversationalAiSettings?.goalCreationEnabled ?? false;
	const formCreationEnabled = aiSettings?.conversationalAiSettings?.formCreationEnabled ?? false;

	const matches = useMatches();
	const tags: string[] =
		matches.length > 0 ? ((matches[matches.length - 1]?.handle as { tags?: string[] })?.tags ?? []) : [];
	const hiddenByRoute = tags.includes(RouterTags.NO_ASK_PERFORMYARD);
	const forceHidden = useAskPerformYardStore((state) => state.forceHidden);
	const isOnGoalCreationPage = useAskPerformYardStore((state) => state.isOnGoalCreationPage);
	const isOnFormBuilderPage = useAskPerformYardStore((state) => state.isOnFormBuilderPage);
	const isOnCompetencyPage = useAskPerformYardStore((state) => state.isOnCompetencyPage);

	const shouldHide = isInIframe() || hiddenByRoute || forceHidden || enhancedAiPanelEnabled;

	return (
		aiEnabled &&
		!shouldHide &&
		((conversationalEnabled && (askHrEnabled || aiAssistedGoalCreationEnabled || formCreationEnabled)) ||
			(isOnGoalCreationPage && goalCoachEnabled) ||
			(isOnFormBuilderPage && formBuildingCoachEnabled) ||
			(isOnCompetencyPage && competencyAgentVisible))
	);
};

/** Conversational AI chat interface for answering questions from company documents. */
const AskPerformYard: React.FC = () => {
	const shouldShow = useAskPerformYardVisible();
	const documentModal = useAskPerformYardStore((state) => state.documentModal);
	const closeDocumentModal = useAskPerformYardStore((state) => state.closeDocumentModal);
	const resetChat = useAskPerformYardStore((state) => state.resetChat);
	const setHasNoDocuments = useAskPerformYardStore((state) => state.setHasNoDocuments);
	const setHasGoalCategories = useAskPerformYardStore((state) => state.setHasGoalCategories);
	const updateGreetingMessage = useAskPerformYardStore((state) => state.updateGreetingMessage);
	const setIsOnFormBuilderPage = useAskPerformYardStore((state) => state.setIsOnFormBuilderPage);
	const setIsOnCompetencyPage = useAskPerformYardStore((state) => state.setIsOnCompetencyPage);
	// Load AI settings for dynamic greeting
	const { data: aiSettings } = useAiSettings();
	const competencyAgentVisible = useCompetencyAgentVisible();
	const reportBuilderVisible = useReportBuilderVisible();
	const goalCoachEnabled = useGoalCoachEnabled();
	const formBuildingCoachEnabled = useFormBuildingCoachEnabled();
	const isOnGoalCreationPage = useAskPerformYardStore((state) => state.isOnGoalCreationPage);

	// Gate form creation on RBAC, not just company AI setting — checkFormPermission tool gates on the same permission.
	const { data: reviewPermissions } = useRbacPrimaryRolePermissions("review");
	const canCreateForms = reviewPermissions?.includes("reviews:createAdminCompanyForms") ?? false;

	// Mount the report-save hook so its auto-save effect fires when the interviewer submits.
	// Safe to mount unconditionally — the effect only runs when reportBuilder.status === "pending",
	// which never gets set for users without the AI report builder harness.
	useReportSave();
	const matches = useMatches();
	const isOnFormBuilderPage = matches.some((m) =>
		((m.handle as { tags?: string[] } | undefined)?.tags ?? []).includes(RouterTags.FORM_BUILDER)
	);
	useEffect(() => {
		setIsOnFormBuilderPage(isOnFormBuilderPage);
	}, [isOnFormBuilderPage, setIsOnFormBuilderPage]);

	const isOnCompetencyPage = matches.some((match) =>
		((match.handle as { tags?: string[] } | undefined)?.tags ?? []).includes(RouterTags.COMPETENCIES_ADMIN)
	);
	useEffect(() => {
		setIsOnCompetencyPage(isOnCompetencyPage);
	}, [isOnCompetencyPage, setIsOnCompetencyPage]);

	const conversationStarted = useAskPerformYardStore((state) =>
		state.messages.some((message) => message.role === "user")
	);
	const activeInterviewerHarness = useAskPerformYardStore((state) => state.activeInterviewerHarness);
	const setActiveInterviewerHarness = useAskPerformYardStore((state) => state.setActiveInterviewerHarness);
	const deactivateInterviewerHarness = useAskPerformYardStore((state) => state.deactivateInterviewerHarness);
	const pageActivatedHarnessRef = useRef(false);
	useEffect(() => {
		if (!competencyAgentVisible) return;
		if (isOnCompetencyPage) {
			// Pre-set on entry so the intro + "Upload a PDF" route deterministically (skip once a conversation exists).
			if (!conversationStarted) {
				setActiveInterviewerHarness("competency");
				pageActivatedHarnessRef.current = true;
			}
		} else if (pageActivatedHarnessRef.current && activeInterviewerHarness === "competency") {
			// Leaving the page undoes our own pre-activation; classifier-entered interviews are left alone.
			pageActivatedHarnessRef.current = false;
			deactivateInterviewerHarness();
		}
	}, [isOnCompetencyPage, competencyAgentVisible, conversationStarted, activeInterviewerHarness]);

	useEffect(() => {
		if (aiSettings?.conversationalAiSettings) {
			const { askHrEnabled, goalCreationEnabled, formCreationEnabled } = aiSettings.conversationalAiSettings;
			updateGreetingMessage({
				askHrEnabled,
				goalCreationEnabled,
				formCreationEnabled: (formCreationEnabled ?? false) && canCreateForms,
				competencyInterviewerEnabled: competencyAgentVisible,
				reportBuilderEnabled: reportBuilderVisible,
				isOnFormBuilderPage,
				isOnGoalCreationPage,
				isOnCompetencyPage,
				goalCoachingEnabled: goalCoachEnabled,
				formBuildingCoachEnabled: formBuildingCoachEnabled && canCreateForms,
			});
		}
	}, [
		aiSettings,
		canCreateForms,
		competencyAgentVisible,
		reportBuilderVisible,
		isOnFormBuilderPage,
		isOnGoalCreationPage,
		goalCoachEnabled,
		formBuildingCoachEnabled,
		isOnCompetencyPage,
		updateGreetingMessage,
	]);

	// Check if the company has goal categories configured
	const { data: goalCategoriesData } = useHasGoalCategories();

	useEffect(() => {
		if (goalCategoriesData !== undefined) {
			setHasGoalCategories(goalCategoriesData.hasCategories);
		}
	}, [goalCategoriesData, setHasGoalCategories]);

	// Check if the user has any AI-accessible documents
	const { data: accessibleDocs } = useHasAccessibleDocuments();

	useEffect(() => {
		if (accessibleDocs !== undefined) {
			setHasNoDocuments(!accessibleDocs.hasDocuments);
		}
	}, [accessibleDocs, setHasNoDocuments]);

	// Get current user ID to detect super user switches
	const userId = useSelector(getUserID);
	const prevUserIdRef = useRef(userId);

	// Reset chat when user ID changes (e.g., during super usering)
	useEffect(() => {
		if (prevUserIdRef.current !== userId && prevUserIdRef.current !== undefined) {
			resetChat();
		}
		prevUserIdRef.current = userId;
	}, [userId, resetChat]);

	if (!shouldShow) {
		return null;
	}

	return (
		<ChatErrorBoundary>
			<GoalCoachPopup />
			<FormBuildingCoachPopup />
			<CompetencyCoachPopup />
			<ChatTrigger />
			<ChatContainer />
			<ViewDocumentModal
				show={documentModal.isOpen}
				onClose={closeDocumentModal}
				document={documentModal.document}
			/>
		</ChatErrorBoundary>
	);
};

export default AskPerformYard;
